<template>
  <Modal>
    <Modal.Title>Remove access?</Modal.Title>
    <Modal.Body>
      Are you sure you want to remove {{ user }}'s access to this file?
    </Modal.Body>
    <Modal.Footer>
      <Button
        variant="stroke"
        :size="isMobile ? 'lg' : 'md'"
        :disabled="loading"
        @click="close()"
      >
        Cancel
      </Button>
      <Button
        variant="danger"
        :size="isMobile ? 'lg' : 'md'"
        :loading="loading"
        @click="handleClick"
      >
        Remove
      </Button>
    </Modal.Footer>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Modal, Button, useModal } from '@dacr/library'
import { graphql } from '@/shared/graphql/router'
import {
  InviteType,
  type ViewersConnection,
  type InviteInput,
} from '@/shared/graphql/router/graphql'
import type { Reference } from '@apollo/client'
import { useMutation } from '@vue/apollo-composable'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useUserStore } from '@/shared/stores/user'
import { useDevice } from '@/shared/composables/useDevice'

const REMOVE_VIEWER_MUTATION = graphql(`
  mutation RemoveViewer($input: RemoveViewerInput!) {
    removeViewer(input: $input) {
      success
    }
  }
`)

const { close } = useModal()
const { isMobile } = useDevice()

const props = defineProps<{
  fileId: string
  invite: InviteInput
  name: string
}>()

const { mutate: removeViewer, loading } = useMutation(REMOVE_VIEWER_MUTATION, {
  clientId: 'router',
})

const toastStore = useToastStore()
const userStore = useUserStore()

const user = computed(() =>
  props.invite.id === userStore.user?.id ? 'your' : `${props.name}`
)

const handleClick = async () => {
  try {
    await removeViewer(
      { input: { fileId: props.fileId, invite: props.invite } },
      {
        update(cache) {
          cache.modify({
            id: cache.identify({ __typename: 'File', id: props.fileId }),
            fields: {
              viewers(existing, { readField }) {
                const data = existing as ViewersConnection
                if (!data?.edges) return existing

                return {
                  ...data,
                  edges: data.edges.filter(({ node }) => {
                    if (props.invite.type === InviteType.Email) {
                      return readField('email', node) !== props.invite.email
                    }

                    const entity = readField<Reference>('entity', node)
                    return entity
                      ? readField('id', entity) !== props.invite.id
                      : true
                  }),
                }
              },
            },
          })
        },
      }
    )

    close()
  } catch (error) {
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Something went wrong - please try again later',
      error: error as Error,
    })
  }
}
</script>
